// Phase 4C / Iter91 — WES Context Builder Tile.
//
// Operator-facing wrapper around the Phase 4B backend endpoint:
//   POST /api/forge/toolbox/wes-context/build
//
// Builds a transient, normalized, read-only WES Context envelope for
// a small batch of symbols and displays it as a per-symbol card grid.
// Surfaces the FROZEN `wes_parity_engine` output (VAL / POC / VAH +
// Δ-VAL / Δ-VAH + WES-P / WES-C + ATR for both 1H + D timeframes)
// + `evaluator_wes_payload` + `engine_source_fingerprint`. Operator
// can copy / download the envelope. Nothing is persisted.
//
// HARD CONSTRAINTS (asserted by Iter91 tests):
//  * Manual-run only. No HTTP on mount. No effect hooks of any kind.
//  * Exactly one outbound POST per click, targeting only the Phase
//    4B WES context endpoint above.
//  * Never calls the Wheel ITM endpoint, the WES-GEX endpoint, the
//    Phase 3B snapshot endpoint, any Market Scanner control plane
//    route, any scanner preview endpoint, any broker / account /
//    position / journal / admin / trade / order endpoint.
//  * No ranking, no winner output, no operator-bypass language
//    outside disclaimers + their testids.
//  * Per-panel wiring into BPCS / BCCS / IC / CFM Shares / CFM
//    LEAPS auto-scan payloads is NOT in scope — explicitly deferred.

import React, { useState } from "react";
import axios from "axios";
import {
  ChevronDown, ChevronUp, Play, Eraser, Copy, Download,
  ShieldAlert, Activity, AlertTriangle,
} from "lucide-react";
import { API } from "@/App";
import { useWESContext } from "@/contexts/WESContextProvider";

const ENDPOINT = "/forge/toolbox/wes-context/build";

const DEFAULT_SYMBOLS = "SPY";
const WES_CONTEXT_EXPORT_VERSION = "phase4c_wes_context_export_v1";

const PRIMARY_TIMEFRAMES = Object.freeze([
  { id: "1H", label: "1H (intraday)",
    testid: "wes-context-builder-primary-1H" },
]);
const CONTEXT_TIMEFRAMES = Object.freeze([
  { id: "D", label: "D (daily)",
    testid: "wes-context-builder-context-D" },
]);

function parseSymbols(text) {
  return Array.from(new Set(
    (text || "")
      .split(/[\s,;\n]+/)
      .map(s => s.trim().toUpperCase())
      .filter(Boolean)
  ));
}

function relativeSeconds(iso) {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  return `${s}s ago`;
}

function shortFingerprint(fp) {
  if (!fp || typeof fp !== "string") return null;
  // Show sha256:<first 12 chars>…
  const m = fp.match(/^sha256:([0-9a-f]+)$/i);
  if (m) return `sha256:${m[1].slice(0, 12)}…`;
  return fp.slice(0, 20);
}

function fmtNum(v, digits = 2) {
  if (v == null || Number.isNaN(v)) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(digits);
}

function fmtPct(v, digits = 2) {
  if (v == null || Number.isNaN(v)) return "—";
  return `${(Number(v) * 100).toFixed(digits)}%`;
}

export default function WESContextBuilderTile() {
  const { setWESContext: publishToContext } = useWESContext();
  const [open, setOpen] = useState(false);
  const [symbolsText, setSymbolsText] = useState(DEFAULT_SYMBOLS);
  const [primary, setPrimary] = useState("1H");
  const [contextTf, setContextTf] = useState("D");
  const [requireClosedBar, setRequireClosedBar] = useState(true);
  const [maxSymbols, setMaxSymbols] = useState(25);

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [wesContext, setWESContextLocal] = useState(null);
  const [requestPayload, setRequestPayload] = useState(null);

  async function buildWESContext() {
    setErrorMsg(null);
    const symbols = parseSymbols(symbolsText);
    if (symbols.length === 0) {
      setErrorMsg("Enter at least one symbol.");
      return;
    }
    const cap = Math.max(1, Math.min(25, Number(maxSymbols) || 25));
    if (symbols.length > cap) {
      setErrorMsg(`Hard cap is ${cap} symbols (got ${symbols.length}).`);
      return;
    }
    const payload = {
      symbols,
      primary,
      context: contextTf,
      max_symbols: cap,
      // Operator preference; backend currently treats the engine's
      // confirmed-bar gate as authoritative. Surfacing the flag here
      // means a future evaluator can read it from the request log.
      require_closed_bar: !!requireClosedBar,
    };
    setRequestPayload(payload);
    setLoading(true);
    try {
      const r = await axios.post(`${API}${ENDPOINT}`, payload);
      setWESContextLocal(r.data);
      // Phase 4C — publish to in-memory WESContextProvider. NOT
      // wired into any auto-scan panel yet (deferred phase).
      publishToContext(r.data, payload);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map(d => d.msg || JSON.stringify(d)).join("; ")
          : (e?.message || "Network error");
      setErrorMsg(msg);
      setWESContextLocal(null);
    } finally {
      setLoading(false);
    }
  }

  function clearWESContext() {
    setWESContextLocal(null);
    setErrorMsg(null);
    setRequestPayload(null);
    publishToContext(null, null);
  }

  async function copyJson() {
    const blob = buildExportEnvelope();
    try {
      await navigator.clipboard.writeText(JSON.stringify(blob, null, 2));
    } catch (_e) {
      // best-effort; do not call backend
    }
  }

  function downloadJson() {
    const blob = buildExportEnvelope();
    const fname = exportFilename(blob);
    const data = JSON.stringify(blob, null, 2);
    const url = URL.createObjectURL(
      new Blob([data], { type: "application/json" })
    );
    const a = document.createElement("a");
    a.href = url;
    a.download = fname;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  function buildExportEnvelope() {
    return {
      wes_context_export_version: WES_CONTEXT_EXPORT_VERSION,
      exported_at: new Date().toISOString(),
      source_panel: "WESContextBuilderTile",
      endpoint: `/api${ENDPOINT}`,
      request_payload_used: requestPayload,
      response_wes_context: wesContext,
      read_only: true,
      execution_enabled: false,
      journal_mutation_enabled: false,
      broker_execution_enabled: false,
      disclaimer:
        "Read-only WES context envelope. No scanner run. No strategy " +
        "ranking. No winner output. No trade execution. No journal " +
        "write. No persistence. No broker or account fetch. Manual " +
        "WES context only. Frozen WES engine source.",
    };
  }

  function exportFilename(blob) {
    const syms = (blob.request_payload_used?.symbols || []).slice(0, 4).join("-")
      || "batch";
    const ts = new Date().toISOString().replace(/[:.]/g, "-");
    return `arxmentis_wes_context_${syms}_${ts}.json`;
  }

  return (
    <div
      data-testid="wes-context-builder-tile"
      className="mb-5 rounded-sm border border-[#26262B] bg-[#0A0A0E]"
    >
      <button
        type="button"
        data-testid="wes-context-builder-toggle"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-[#121216]"
      >
        <Activity size={16} className="text-[#E0E0E0]" />
        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#E0E0E0]">
          WES Context Builder
        </span>
        <span className="text-[9px] font-mono uppercase tracking-[0.12em] text-[#5F5F66]">
          · phase 4c · manual wes context · frozen engine source · no scanner run · no execution
        </span>
        <span className="ml-auto text-[#5F5F66]">
          {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </span>
      </button>

      {open && (
        <div className="border-t border-[#26262B]">
          {/* Safety banner */}
          <div
            data-testid="wes-context-builder-banner"
            className="px-4 py-2 border-b border-[#26262B] bg-[#FFB800]/5 text-[#FFB800] text-[10px] font-mono flex items-center gap-3 flex-wrap"
          >
            <ShieldAlert size={12} />
            <span data-testid="wes-context-builder-banner-manual">
              Manual WES context only.
            </span>
            <span className="text-[#FFB800]/40">·</span>
            <span data-testid="wes-context-builder-banner-no-scanner-run">
              No scanner run.
            </span>
            <span className="text-[#FFB800]/40">·</span>
            <span data-testid="wes-context-builder-banner-no-ranking">
              No strategy ranking.
            </span>
            <span className="text-[#FFB800]/40">·</span>
            <span data-testid="wes-context-builder-banner-no-execution">
              No trade execution.
            </span>
            <span className="text-[#FFB800]/40">·</span>
            <span data-testid="wes-context-builder-banner-no-journal">
              No journal write.
            </span>
            <span className="text-[#FFB800]/40">·</span>
            <span data-testid="wes-context-builder-banner-no-persistence">
              No persistence.
            </span>
            <span className="text-[#FFB800]/40">·</span>
            <span data-testid="wes-context-builder-banner-no-account-fetch">
              No account / position fetch.
            </span>
            <span className="text-[#FFB800]/40">·</span>
            <span data-testid="wes-context-builder-banner-frozen-engine">
              Frozen WES engine source.
            </span>
          </div>

          {/* Inputs */}
          <div className="px-4 py-3 border-b border-[#26262B] space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">
                  Symbols (comma / space / newline · max 25)
                </span>
                <textarea
                  data-testid="wes-context-builder-symbols"
                  value={symbolsText}
                  onChange={e => setSymbolsText(e.target.value)}
                  rows={2}
                  spellCheck={false}
                  className="w-full mt-1 bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-1 text-[11px] font-mono text-[#E0E0E0] focus:outline-none"
                />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">
                    Primary timeframe
                  </span>
                  <select
                    data-testid="wes-context-builder-primary"
                    value={primary}
                    onChange={e => setPrimary(e.target.value)}
                    className="w-full mt-1 bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-1 text-[11px] font-mono text-[#E0E0E0] focus:outline-none"
                  >
                    {PRIMARY_TIMEFRAMES.map(p => (
                      <option
                        key={p.id} value={p.id}
                        data-testid={p.testid}
                      >
                        {p.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">
                    Context timeframe
                  </span>
                  <select
                    data-testid="wes-context-builder-context"
                    value={contextTf}
                    onChange={e => setContextTf(e.target.value)}
                    className="w-full mt-1 bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-1 text-[11px] font-mono text-[#E0E0E0] focus:outline-none"
                  >
                    {CONTEXT_TIMEFRAMES.map(c => (
                      <option
                        key={c.id} value={c.id}
                        data-testid={c.testid}
                      >
                        {c.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="block">
                <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">
                  Require closed bar
                </span>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="checkbox"
                    data-testid="wes-context-builder-require-closed-bar"
                    checked={requireClosedBar}
                    onChange={e => setRequireClosedBar(e.target.checked)}
                    className="accent-[#E0E0E0]"
                  />
                  <span className="text-[10px] font-mono text-[#8A8A93]">
                    Surface BAR_UNCONFIRMED warning when engine reports a forming bar.
                  </span>
                </div>
              </label>
              <label className="block">
                <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93]">
                  Max symbols (hard cap 25)
                </span>
                <input
                  type="number"
                  min={1}
                  max={25}
                  step={1}
                  data-testid="wes-context-builder-max-symbols"
                  value={maxSymbols}
                  onChange={e => setMaxSymbols(e.target.value)}
                  className="w-full mt-1 bg-[#08080A] border border-[#26262B] rounded-sm px-2 py-1 text-[11px] font-mono text-[#E0E0E0] focus:outline-none"
                />
              </label>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                data-testid="wes-context-builder-run"
                onClick={buildWESContext}
                disabled={loading}
                className="px-3 py-1 rounded-sm border border-[#E0E0E0]/30 bg-[#E0E0E0]/10 text-[10px] font-mono uppercase tracking-[0.14em] text-[#E0E0E0] hover:bg-[#E0E0E0]/20 inline-flex items-center gap-1 disabled:opacity-50"
              >
                <Play size={10} />
                {loading ? "Building WES context…" : "Build WES Context"}
              </button>
              <button
                type="button"
                data-testid="wes-context-builder-clear"
                onClick={clearWESContext}
                className="px-2 py-1 rounded-sm border border-[#26262B] text-[9px] font-mono uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
              >
                <Eraser size={10} /> Clear WES Context
              </button>
              {wesContext && (
                <>
                  <button
                    type="button"
                    data-testid="wes-context-builder-copy"
                    onClick={copyJson}
                    className="px-2 py-1 rounded-sm border border-[#26262B] text-[9px] font-mono uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
                  >
                    <Copy size={10} /> Copy WES Context JSON
                  </button>
                  <button
                    type="button"
                    data-testid="wes-context-builder-download"
                    onClick={downloadJson}
                    className="px-2 py-1 rounded-sm border border-[#26262B] text-[9px] font-mono uppercase tracking-[0.12em] text-[#8A8A93] hover:text-[#E0E0E0] hover:border-[#3F3F47] inline-flex items-center gap-1"
                  >
                    <Download size={10} /> Download WES Context JSON
                  </button>
                </>
              )}
            </div>

            {errorMsg && (
              <div
                data-testid="wes-context-builder-error"
                className="flex items-start gap-2 text-[10px] font-mono text-[#FF3366] border border-[#FF3366]/30 bg-[#FF3366]/5 px-2 py-1.5 rounded-sm"
              >
                <AlertTriangle size={11} className="shrink-0 mt-0.5" />
                <span>{errorMsg}</span>
              </div>
            )}
          </div>

          {/* Output */}
          {!wesContext && !loading && !errorMsg && (
            <div
              data-testid="wes-context-builder-empty"
              className="px-4 py-3 text-[10px] font-mono text-[#5F5F66]"
            >
              No WES context yet. Enter symbols and click Build WES Context.
            </div>
          )}

          {wesContext && (
            <WESContextResult
              wesContext={wesContext}
              requestPayload={requestPayload}
            />
          )}
        </div>
      )}
    </div>
  );
}

function WESContextResult({ wesContext, requestPayload }) {
  const rows = wesContext.rows || [];
  const fp = wesContext.engine_source_fingerprint;
  const fpShort = shortFingerprint(fp);
  return (
    <div
      data-testid="wes-context-builder-result"
      className="px-4 py-3 space-y-3"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] font-mono">
        <span
          data-testid="wes-context-builder-result-version"
          className="text-[#5F5F66]"
        >
          version
          <span className="text-[#E0E0E0] ml-2">{wesContext.wes_context_version}</span>
        </span>
        <span
          data-testid="wes-context-builder-result-context-id"
          className="text-[#5F5F66]"
        >
          context_id
          <span className="text-[#E0E0E0] ml-2">{wesContext.wes_context_id}</span>
        </span>
        <span
          data-testid="wes-context-builder-result-as-of"
          className="text-[#5F5F66]"
        >
          as_of
          <span className="text-[#E0E0E0] ml-2">{wesContext.as_of}</span>
          <span className="text-[#5F5F66] ml-2">({relativeSeconds(wesContext.as_of)})</span>
        </span>
        {fpShort ? (
          <span
            data-testid="wes-context-builder-result-engine-fingerprint"
            title={fp || ""}
            className="px-1.5 py-0.5 rounded-sm border border-[#00FF88]/30 bg-[#00FF88]/5 text-[#00FF88] text-[9px] uppercase tracking-[0.12em]"
          >
            WES engine fingerprint: {fpShort}
          </span>
        ) : (
          <span
            data-testid="wes-context-builder-result-engine-fingerprint-missing"
            className="px-1.5 py-0.5 rounded-sm border border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800] text-[9px] uppercase tracking-[0.12em]"
          >
            WES engine fingerprint unavailable.
          </span>
        )}
        <Flag label="read_only"               value={wesContext.read_only} testid="wes-context-builder-result-flag-read-only" />
        <Flag label="execution_enabled"       value={wesContext.execution_enabled} testid="wes-context-builder-result-flag-execution-enabled" />
        <Flag label="journal_mutation_enabled" value={wesContext.journal_mutation_enabled} testid="wes-context-builder-result-flag-journal-mutation-enabled" />
        <Flag label="broker_execution_enabled" value={wesContext.broker_execution_enabled} testid="wes-context-builder-result-flag-broker-execution-enabled" />
        {wesContext.rate_limited && (
          <span
            data-testid="wes-context-builder-result-rate-limited"
            className="px-1.5 py-0.5 rounded-sm border border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800]"
          >
            RATE_LIMITED
          </span>
        )}
        {wesContext.partial && (
          <span
            data-testid="wes-context-builder-result-partial"
            className="px-1.5 py-0.5 rounded-sm border border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800]"
          >
            PARTIAL
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map(row => (
          <WESContextRowCard key={row.symbol} row={row} />
        ))}
      </div>

      <details
        data-testid="wes-context-builder-full-json"
        className="border border-[#26262B] rounded-sm"
      >
        <summary className="cursor-pointer px-2 py-1 text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0]">
          Full WES context JSON (raw response)
        </summary>
        <pre className="mt-1 text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#08080A] p-2 max-h-96 overflow-auto">
          {JSON.stringify(wesContext, null, 2)}
        </pre>
      </details>

      <details
        data-testid="wes-context-builder-request-json"
        className="border border-[#26262B] rounded-sm"
      >
        <summary className="cursor-pointer px-2 py-1 text-[10px] font-mono uppercase tracking-[0.14em] text-[#8A8A93] hover:text-[#E0E0E0]">
          Request payload used
        </summary>
        <pre className="mt-1 text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#08080A] p-2 max-h-48 overflow-auto">
          {JSON.stringify(requestPayload, null, 2)}
        </pre>
      </details>
    </div>
  );
}

function Flag({ label, value, testid }) {
  const cls = label === "read_only"
    ? (value === true
        ? "border-[#00FF88]/40 bg-[#00FF88]/5 text-[#00FF88]"
        : "border-[#FF3366]/40 bg-[#FF3366]/5 text-[#FF3366]")
    : (value === true
        ? "border-[#FF3366]/40 bg-[#FF3366]/5 text-[#FF3366]"
        : "border-[#26262B] text-[#8A8A93]");
  return (
    <span
      data-testid={testid}
      className={`px-1.5 py-0.5 rounded-sm border text-[9px] uppercase tracking-[0.12em] ${cls}`}
    >
      {label}: {String(value)}
    </span>
  );
}

function WESContextRowCard({ row }) {
  const sym = row.symbol;
  const h = row.h || {};
  const d = row.d || {};
  return (
    <div
      data-testid={`wes-context-builder-row-${sym}`}
      className="rounded-sm border border-[#26262B] bg-[#08080A] p-3 text-[10px] font-mono space-y-1"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span
          data-testid={`wes-context-builder-row-${sym}-symbol`}
          className="text-[13px] font-bold uppercase tracking-[0.14em] text-[#E0E0E0]"
        >
          {sym}
        </span>
        <span
          data-testid={`wes-context-builder-row-${sym}-source`}
          className="text-[#8A8A93] uppercase tracking-[0.12em]"
        >
          {row.source}
        </span>
        {row.as_of && (
          <span
            data-testid={`wes-context-builder-row-${sym}-as-of`}
            className="text-[#5F5F66]"
          >
            {relativeSeconds(row.as_of)}
          </span>
        )}
        <span
          data-testid={`wes-context-builder-row-${sym}-bar-closed`}
          className={`px-1.5 py-0.5 rounded-sm border text-[9px] uppercase tracking-[0.12em] ${row.bar_closed
            ? "border-[#00FF88]/30 bg-[#00FF88]/5 text-[#00FF88]"
            : "border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800]"}`}
        >
          bar_closed: {String(row.bar_closed)}
        </span>
        <span
          data-testid={`wes-context-builder-row-${sym}-parity-status`}
          className={`px-1.5 py-0.5 rounded-sm border text-[9px] uppercase tracking-[0.12em] ${row.parity_status === "valid"
            ? "border-[#00FF88]/30 bg-[#00FF88]/5 text-[#00FF88]"
            : "border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800]"}`}
        >
          parity: {row.parity_status || "—"}
        </span>
        {(h.regime || d.regime) && (
          <span
            data-testid={`wes-context-builder-row-${sym}-regime`}
            className="px-1.5 py-0.5 rounded-sm border border-[#26262B] text-[#8A8A93] text-[9px] uppercase tracking-[0.12em]"
          >
            regime: {h.regime || d.regime}
          </span>
        )}
      </div>

      {row.spot_price != null && (
        <div className="text-[#8A8A93]">
          <span data-testid={`wes-context-builder-row-${sym}-spot`}>
            SPOT <span className="text-[#E0E0E0] font-bold">{fmtNum(row.spot_price, 2)}</span>
            {row.spot_source && (
              <span className="ml-1 text-[#5F5F66]">({row.spot_source})</span>
            )}
          </span>
        </div>
      )}

      {/* H block */}
      <div
        data-testid={`wes-context-builder-row-${sym}-h-block`}
        className="border-t border-[#26262B] pt-1 mt-1"
      >
        <div className="text-[9px] uppercase tracking-[0.14em] text-[#5F5F66] mb-1">
          1H — primary timeframe
        </div>
        <div className="grid grid-cols-4 gap-x-3 gap-y-0.5 text-[#8A8A93]">
          <span data-testid={`wes-context-builder-row-${sym}-h-val`}>
            VAL <span className="text-[#E0E0E0] font-bold">{fmtNum(h.val, 2)}</span>
          </span>
          <span data-testid={`wes-context-builder-row-${sym}-h-poc`}>
            POC <span className="text-[#E0E0E0] font-bold">{fmtNum(h.poc, 2)}</span>
          </span>
          <span data-testid={`wes-context-builder-row-${sym}-h-vah`}>
            VAH <span className="text-[#E0E0E0] font-bold">{fmtNum(h.vah, 2)}</span>
          </span>
          <span data-testid={`wes-context-builder-row-${sym}-h-atr`}>
            ATR <span className="text-[#E0E0E0] font-bold">{fmtNum(h.atr, 2)}</span>
          </span>
          <span data-testid={`wes-context-builder-row-${sym}-h-delta-val`}>
            Δ-VAL <span className="text-[#E0E0E0] font-bold">{fmtPct(h.delta_val, 2)}</span>
          </span>
          <span data-testid={`wes-context-builder-row-${sym}-h-delta-vah`}>
            Δ-VAH <span className="text-[#E0E0E0] font-bold">{fmtPct(h.delta_vah, 2)}</span>
          </span>
          <span data-testid={`wes-context-builder-row-${sym}-h-wes-p`}>
            WES-P <span className="text-[#E0E0E0] font-bold">{fmtNum(h.wes_p, 1)}</span>
          </span>
          <span data-testid={`wes-context-builder-row-${sym}-h-wes-c`}>
            WES-C <span className="text-[#E0E0E0] font-bold">{fmtNum(h.wes_c, 1)}</span>
          </span>
        </div>
      </div>

      {/* D block */}
      <div
        data-testid={`wes-context-builder-row-${sym}-d-block`}
        className="border-t border-[#26262B] pt-1 mt-1"
      >
        <div className="text-[9px] uppercase tracking-[0.14em] text-[#5F5F66] mb-1">
          D — context timeframe
        </div>
        <div className="grid grid-cols-4 gap-x-3 gap-y-0.5 text-[#8A8A93]">
          <span data-testid={`wes-context-builder-row-${sym}-d-val`}>
            VAL <span className="text-[#E0E0E0] font-bold">{fmtNum(d.val, 2)}</span>
          </span>
          <span data-testid={`wes-context-builder-row-${sym}-d-poc`}>
            POC <span className="text-[#E0E0E0] font-bold">{fmtNum(d.poc, 2)}</span>
          </span>
          <span data-testid={`wes-context-builder-row-${sym}-d-vah`}>
            VAH <span className="text-[#E0E0E0] font-bold">{fmtNum(d.vah, 2)}</span>
          </span>
          <span data-testid={`wes-context-builder-row-${sym}-d-atr`}>
            ATR <span className="text-[#E0E0E0] font-bold">{fmtNum(d.atr, 2)}</span>
          </span>
          <span data-testid={`wes-context-builder-row-${sym}-d-delta-val`}>
            Δ-VAL <span className="text-[#E0E0E0] font-bold">{fmtPct(d.delta_val, 2)}</span>
          </span>
          <span data-testid={`wes-context-builder-row-${sym}-d-delta-vah`}>
            Δ-VAH <span className="text-[#E0E0E0] font-bold">{fmtPct(d.delta_vah, 2)}</span>
          </span>
          <span data-testid={`wes-context-builder-row-${sym}-d-wes-p`}>
            WES-P <span className="text-[#E0E0E0] font-bold">{fmtNum(d.wes_p, 1)}</span>
          </span>
          <span data-testid={`wes-context-builder-row-${sym}-d-wes-c`}>
            WES-C <span className="text-[#E0E0E0] font-bold">{fmtNum(d.wes_c, 1)}</span>
          </span>
        </div>
      </div>

      {Array.isArray(row.warnings) && row.warnings.length > 0 && (
        <div
          data-testid={`wes-context-builder-row-${sym}-warnings`}
          className="flex items-center gap-1 flex-wrap"
        >
          {row.warnings.map((w, i) => (
            <span
              key={i}
              className="px-1.5 py-0.5 rounded-sm border border-[#FFB800]/40 bg-[#FFB800]/5 text-[#FFB800] text-[9px] uppercase tracking-[0.12em]"
            >
              {w}
            </span>
          ))}
        </div>
      )}

      {Array.isArray(row.errors) && row.errors.length > 0 && (
        <div
          data-testid={`wes-context-builder-row-${sym}-errors`}
          className="flex items-start gap-1 flex-col"
        >
          {row.errors.map((e, i) => (
            <span
              key={i}
              className="text-[#FF3366] text-[9px] break-words"
            >
              {e}
            </span>
          ))}
        </div>
      )}

      <details
        data-testid={`wes-context-builder-row-${sym}-evaluator-payload`}
        className="mt-1"
      >
        <summary className="cursor-pointer text-[9px] uppercase tracking-[0.14em] text-[#5F5F66] hover:text-[#E0E0E0]">
          evaluator_wes_payload (collapsed)
        </summary>
        <pre className="mt-1 text-[10px] font-mono text-[#E0E0E0] whitespace-pre-wrap break-words bg-[#0A0A0E] p-2 max-h-48 overflow-auto">
          {JSON.stringify(row.evaluator_wes_payload || {}, null, 2)}
        </pre>
      </details>
    </div>
  );
}
